import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { pb } from '../api-utils'

type PageState = 'loading' | 'form' | 'submitting' | 'success' | 'error'

const PhonePostCallPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const reservationId = searchParams.get('reservationId')

  const [pageState, setPageState] = useState<PageState>('loading')
  const [reservation, setReservation] = useState<any>(null)
  const [error, setError] = useState('')

  // Form fields
  const [address, setAddress] = useState('')
  const [addressLine2, setAddressLine2] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('OH')
  const [zip, setZip] = useState('')
  const [emergencyName, setEmergencyName] = useState('')
  const [emergencyPhone, setEmergencyPhone] = useState('')
  const [vin, setVin] = useState('')
  const [licPlateState, setLicPlateState] = useState('')
  const [licPlateNumber, setLicPlateNumber] = useState('')
  const [driverLicFront, setDriverLicFront] = useState<File | null>(null)
  const [driverLicBack, setDriverLicBack] = useState<File | null>(null)
  const [proofOfInsurance, setProofOfInsurance] = useState<File | null>(null)

  useEffect(() => {
    if (!reservationId) {
      setError('No reservation ID provided')
      setPageState('error')
      return
    }
    loadReservation()
  }, [reservationId])

  const loadReservation = async () => {
    try {
      const response = await axios.post('/.netlify/functions/phone-get-reservation', {
        reservation_id: reservationId,
      })
      if (!response.data.success) throw new Error(response.data.error)
      setReservation(response.data.reservation)
      setPageState('form')
    } catch (err: any) {
      setError(err.message || 'Failed to load reservation')
      setPageState('error')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!reservationId) return

    setPageState('submitting')
    try {
      // We need the actual PocketBase record IDs to update
      // Use api_clients auth since this is a server-side operation
      const res = await axios.post('/.netlify/functions/phone-update-documents', {
        reservation_id: reservationId,
        address_line_1: address,
        address_line_2: addressLine2,
        city,
        state,
        zip,
        emergency_contact_name: emergencyName,
        emergency_contact_phone: emergencyPhone,
        vin,
        lic_plate_state: licPlateState,
        lic_plate_number: licPlateNumber,
        driver_lic_front: driverLicFront ? await fileToBase64(driverLicFront) : null,
        driver_lic_back: driverLicBack ? await fileToBase64(driverLicBack) : null,
        proof_of_insurance: proofOfInsurance ? await fileToBase64(proofOfInsurance) : null,
      })

      if (!res.data.success) throw new Error(res.data.error)
      setPageState('success')
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update')
      setPageState('error')
    }
  }

  const fileToBase64 = (file: File): Promise<{ name: string; type: string; base64: string }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve({ name: file.name, type: file.type, base64: reader.result as string })
      reader.onerror = reject
    })
  }

  const containerStyle: React.CSSProperties = {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '2rem 1.5rem',
    minHeight: '60vh',
  }
  const cardStyle: React.CSSProperties = {
    background: '#fff',
    borderRadius: '12px',
    padding: '2rem',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  }
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '1rem',
    marginBottom: '1rem',
  }
  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontWeight: 600,
    color: '#232043',
    marginBottom: '0.25rem',
    fontSize: '0.9rem',
  }

  if (pageState === 'loading') {
    return <div style={containerStyle}><div style={cardStyle}><p>Loading...</p></div></div>
  }

  if (pageState === 'error') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ color: '#e74c3c' }}>Error</h2>
          <p style={{ color: '#666' }}>{error}</p>
          <p>Call us at <a href="tel:6146992495">(614) 699-2495</a></p>
        </div>
      </div>
    )
  }

  if (pageState === 'success') {
    return (
      <div style={containerStyle}>
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <h2 style={{ color: '#27ae60' }}>All done!</h2>
          <p style={{ color: '#666', marginTop: '1rem' }}>
            Your documents have been uploaded. Your gate access code will be sent via text once everything is verified.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h2 style={{ color: '#232043', marginBottom: '0.5rem' }}>
          Complete Your Profile
        </h2>
        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
          Welcome back{reservation?.customer_name ? `, ${reservation.customer_name.split(' ')[0]}` : ''}! Please provide the remaining details for your reservation.
        </p>

        <form onSubmit={handleSubmit}>
          <h3 style={{ color: '#232043', fontSize: '1rem', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>
            Address
          </h3>
          <label style={labelStyle}>Street Address *</label>
          <input style={inputStyle} value={address} onChange={e => setAddress(e.target.value)} required />
          <label style={labelStyle}>Apt / Suite</label>
          <input style={inputStyle} value={addressLine2} onChange={e => setAddressLine2(e.target.value)} />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <div style={{ flex: 2 }}>
              <label style={labelStyle}>City *</label>
              <input style={inputStyle} value={city} onChange={e => setCity(e.target.value)} required />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>State *</label>
              <input style={inputStyle} value={state} onChange={e => setState(e.target.value)} required maxLength={2} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>ZIP *</label>
              <input style={inputStyle} value={zip} onChange={e => setZip(e.target.value)} required />
            </div>
          </div>

          <h3 style={{ color: '#232043', fontSize: '1rem', marginBottom: '1rem', marginTop: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>
            Emergency Contact
          </h3>
          <label style={labelStyle}>Name *</label>
          <input style={inputStyle} value={emergencyName} onChange={e => setEmergencyName(e.target.value)} required />
          <label style={labelStyle}>Phone *</label>
          <input style={inputStyle} value={emergencyPhone} onChange={e => setEmergencyPhone(e.target.value)} required />

          <h3 style={{ color: '#232043', fontSize: '1rem', marginBottom: '1rem', marginTop: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>
            Vehicle Details
          </h3>
          <label style={labelStyle}>VIN *</label>
          <input style={inputStyle} value={vin} onChange={e => setVin(e.target.value)} required />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>License Plate State *</label>
              <input style={inputStyle} value={licPlateState} onChange={e => setLicPlateState(e.target.value)} required maxLength={2} />
            </div>
            <div style={{ flex: 2 }}>
              <label style={labelStyle}>License Plate Number *</label>
              <input style={inputStyle} value={licPlateNumber} onChange={e => setLicPlateNumber(e.target.value)} required />
            </div>
          </div>

          <h3 style={{ color: '#232043', fontSize: '1rem', marginBottom: '1rem', marginTop: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>
            Document Uploads
          </h3>
          <label style={labelStyle}>Driver's License (Front) *</label>
          <input style={inputStyle} type="file" accept="image/*,.pdf" onChange={e => setDriverLicFront(e.target.files?.[0] || null)} required />
          <label style={labelStyle}>Driver's License (Back) *</label>
          <input style={inputStyle} type="file" accept="image/*,.pdf" onChange={e => setDriverLicBack(e.target.files?.[0] || null)} required />
          <label style={labelStyle}>Proof of Insurance *</label>
          <input style={inputStyle} type="file" accept="image/*,.pdf" onChange={e => setProofOfInsurance(e.target.files?.[0] || null)} required />

          <button
            type="submit"
            disabled={pageState === 'submitting'}
            style={{
              width: '100%',
              marginTop: '1.5rem',
              background: '#232043',
              color: 'yellow',
              padding: '1rem',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '1.1rem',
              cursor: pageState === 'submitting' ? 'not-allowed' : 'pointer',
              opacity: pageState === 'submitting' ? 0.7 : 1,
            }}
          >
            {pageState === 'submitting' ? 'Uploading...' : 'Submit Documents'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default PhonePostCallPage
